import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarCheck2, Plus, Edit, Trash, RefreshCw, Save, Send, CheckCircle2, XCircle, Ban, Upload, FileSpreadsheet, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import { LEAVE_STATUSES, LEAVE_TYPES } from '../payroll/core/leaveHoliday';
import {
    approveLeaveRequest, cancelLeaveRequest, checkLeaveRequest, deleteLeaveRequest, importLeaveBalances, initializeLeaveYear,
    loadLeaveData, rejectLeaveRequest, saveLeaveBalance, saveLeaveRequest, submitLeaveRequest,
} from '../payroll/data/leaveActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import { downloadWorkbook, readFirstSheet } from '../payroll/components/excelIO';
import { ReasonModal } from '../payroll/components/PeriodDialogs';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, NumberInput, PageShell,
    Pagination, SearchSelect, SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, employeeOptions, formatDay,
    fromInputDate, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, toInputDate, usePagination, useSortedRows,
} from '../payroll/components/ui';

const BALANCE_HEADER = ['SỐ HĐ', 'HỌ TÊN', 'NĂM', 'PHÉP CHUẨN', 'PHÉP TỒN NĂM TRƯỚC', 'PHÉP ĐIỀU CHỈNH', 'GHI CHÚ'];
const days = (value) => (Number(value) || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 });
const isWorking = (employee) => employee.SO_HD && employee.TRANG_THAI_LAM_VIEC !== 'Đã nghỉ';

function LeaveRequestForm({ initial, isNew, data, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const from = fromInputDate(form.from);
    const to = fromInputDate(form.to) || from;
    const request = { MA_DON: form.MA_DON, NHAN_SU_ID: form.NHAN_SU_ID, LOAI_PHEP: form.LOAI_PHEP, TU_NGAY: from, DEN_NGAY: to, NUA_NGAY: form.halfDay && from && to && from.getTime() === to.getTime(), LY_DO: form.LY_DO };
    const check = checkLeaveRequest(data, request);
    const balance = data.balances.find((item) => item.NHAN_SU_ID === form.NHAN_SU_ID && from && item.NAM === from.getFullYear());
    const singleDay = from && to && from.getTime() === to.getTime();

    const save = async (submit) => {
        try {
            setSaving(true);
            await saveLeaveRequest(data, request, { isNew, submit });
            toast.success(submit ? 'Đã lưu và gửi duyệt đơn nghỉ' : 'Đã lưu nháp đơn nghỉ');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={isNew ? 'Tạo đơn nghỉ' : 'Sửa đơn nghỉ'} subtitle="Chủ nhật và ngày lễ hưởng lương không bị tính vào đơn" icon={CalendarCheck2} onClose={onClose} disabled={saving} size="max-w-3xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                    <button className={buttonSecondary} onClick={() => save(false)} disabled={saving || check.errors.length > 0}><Save className="w-4 h-4" />Lưu nháp</button>
                    <button className={buttonPrimary} onClick={() => save(true)} disabled={saving || check.errors.length > 0}><Send className="w-4 h-4" />Lưu và gửi duyệt</button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <Field label="Nhân sự" required>
                        <SearchSelect options={employeeOptions(data.employees.filter(isWorking))} value={form.NHAN_SU_ID} disabled={!isNew} onChange={(value) => set({ NHAN_SU_ID: value })} placeholder="Tìm tên hoặc Số HĐ..." maxHeight="max-h-40" />
                    </Field>
                    <Field label="Loại nghỉ" required>
                        <select className={inputClass} value={form.LOAI_PHEP} onChange={(e) => set({ LOAI_PHEP: e.target.value })}>
                            {Object.entries(LEAVE_TYPES).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Từ ngày" required><input type="date" className={inputClass} value={form.from} onChange={(e) => set({ from: e.target.value })} /></Field>
                        <Field label="Đến ngày" hint="Bỏ trống nếu một ngày"><input type="date" className={inputClass} value={form.to} onChange={(e) => set({ to: e.target.value })} /></Field>
                    </div>
                    {singleDay && (
                        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.halfDay} onChange={(e) => set({ halfDay: e.target.checked })} />Nghỉ nửa ngày (0,5)</label>
                    )}
                    <Field label="Lý do"><textarea rows={2} className={inputClass} value={form.LY_DO} onChange={(e) => set({ LY_DO: e.target.value })} /></Field>
                </div>
                <div className="space-y-3 text-sm">
                    <div className="rounded-lg bg-indigo-50 p-3">
                        <p className="text-indigo-700 text-xs">Số ngày tính</p>
                        <p className="text-2xl font-bold text-indigo-800">{days(check.total)} ngày</p>
                        <p className="text-xs text-indigo-700">{LEAVE_TYPES[form.LOAI_PHEP]?.deduct ? 'Trừ vào phép năm, tính vào ngày phép hưởng lương (PN)' : LEAVE_TYPES[form.LOAI_PHEP]?.paid ? 'Không trừ phép năm, vẫn tính vào ngày phép hưởng lương (PN)' : 'Không trừ phép năm, không hưởng lương'}</p>
                    </div>
                    {LEAVE_TYPES[form.LOAI_PHEP]?.deduct && form.NHAN_SU_ID && (
                        balance
                            ? <p className="text-gray-700">Sổ phép {balance.NAM}: được dùng <b>{days(balance.entitled)}</b>, đã dùng <b>{days(balance.used)}</b>, chờ duyệt <b>{days(balance.pending)}</b>, còn lại <b>{days(balance.remaining)}</b>.</p>
                            : null
                    )}
                    {check.days.length > 0 && check.days.length <= 31 && (
                        <div className="flex flex-wrap gap-1">
                            {check.days.map((day) => (
                                <span key={day.ngay} title={day.cn ? 'Chủ nhật' : day.le ? 'Ngày lễ' : `${day.soNgay} ngày`}
                                    className={`px-1.5 py-0.5 rounded text-xs ${day.soNgay ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400 line-through'}`}>
                                    {day.ngay.slice(8, 10)}/{day.ngay.slice(5, 7)}{day.le ? ' lễ' : day.cn ? ' CN' : day.soNgay === 0.5 ? ' ½' : ''}
                                </span>
                            ))}
                        </div>
                    )}
                    <MessageList items={check.errors} />
                    <MessageList tone="yellow" items={check.warnings} />
                </div>
            </div>
        </Modal>
    );
}

function BalanceForm({ initial, isNew, employees, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const entitled = (parseSheetNumber(form.standard, 0) || 0) + (parseSheetNumber(form.carry, 0) || 0) + (parseSheetNumber(form.adjust, 0) || 0);
    const handleSave = async () => {
        try {
            setSaving(true);
            await saveLeaveBalance({ NHAN_SU_ID: form.NHAN_SU_ID, NAM: form.NAM, PHEP_CHUAN: parseSheetNumber(form.standard, 0), PHEP_TON_NAM_TRUOC: parseSheetNumber(form.carry, 0), PHEP_DIEU_CHINH: parseSheetNumber(form.adjust, 0), GHI_CHU: form.GHI_CHU }, { isNew });
            toast.success('Đã lưu sổ phép');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };
    return (
        <Modal title={isNew ? `Tạo sổ phép năm ${form.NAM}` : `Sửa sổ phép năm ${form.NAM}`} icon={BookOpen} onClose={onClose} disabled={saving} size="max-w-lg"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                    <button className={buttonPrimary} onClick={handleSave} disabled={saving || !form.NHAN_SU_ID}><Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu'}</button>
                </>
            )}>
            <div className="space-y-4">
                <Field label="Nhân sự" required>
                    <SearchSelect options={employeeOptions(employees)} value={form.NHAN_SU_ID} disabled={!isNew} onChange={(value) => set({ NHAN_SU_ID: value })} placeholder="Tìm tên hoặc Số HĐ..." maxHeight="max-h-40" />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Phép chuẩn"><NumberInput value={form.standard} onChange={(next) => set({ standard: next })} /></Field>
                    <Field label="Tồn năm trước"><NumberInput value={form.carry} onChange={(next) => set({ carry: next })} /></Field>
                    <Field label="Điều chỉnh" hint="Âm để trừ"><NumberInput value={form.adjust} onChange={(next) => set({ adjust: next })} /></Field>
                </div>
                <p className="text-sm text-gray-700">Được dùng trong năm: <b>{days(Math.max(0, entitled))} ngày</b>. Số đã dùng tính từ các đơn phép năm đã duyệt.</p>
                <Field label="Ghi chú"><input className={inputClass} value={form.GHI_CHU} onChange={(e) => set({ GHI_CHU: e.target.value })} /></Field>
            </div>
        </Modal>
    );
}

function InitializeModal({ year, onClose, onSubmit }) {
    const [standard, setStandard] = useState('12');
    const [carryOver, setCarryOver] = useState(true);
    const [busy, setBusy] = useState(false);
    return (
        <Modal title={`Khởi tạo sổ phép năm ${year}`} icon={BookOpen} onClose={onClose} disabled={busy} size="max-w-md"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Hủy</button>
                    <button className={buttonPrimary} disabled={busy} onClick={async () => { try { setBusy(true); await onSubmit(parseSheetNumber(standard, 12), carryOver); } finally { setBusy(false); } }}>Khởi tạo</button>
                </>
            )}>
            <div className="space-y-4 text-sm">
                <p className="text-gray-600">Tạo sổ cho mọi nhân sự đang làm chưa có sổ năm {year}. Người đã có sổ giữ nguyên.</p>
                <Field label="Phép chuẩn mỗi người (ngày)" hint="Luật lao động: 12 ngày/năm cho công việc bình thường"><NumberInput value={standard} onChange={setStandard} /></Field>
                <label className="flex items-center gap-2 text-gray-700"><input type="checkbox" checked={carryOver} onChange={(e) => setCarryOver(e.target.checked)} />Chuyển số phép còn lại năm {year - 1} sang làm phép tồn</label>
            </div>
        </Modal>
    );
}

function ImportBalanceModal({ data, year, onClose, onDone }) {
    const [rows, setRows] = useState(null);
    const [saving, setSaving] = useState(false);
    const employeeBySoHd = new Map(data.employees.map((employee) => [employee.SO_HD, employee]));
    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const raw = await readFirstSheet(file);
            const seen = new Set();
            setRows(raw.filter((row) => String(row['SỐ HĐ'] ?? '').trim()).map((row, index) => {
                const soHd = String(row['SỐ HĐ']).trim();
                const value = {
                    NHAN_SU_ID: soHd, NAM: Math.trunc(parseSheetNumber(row['NĂM'], year)),
                    PHEP_CHUAN: parseSheetNumber(row['PHÉP CHUẨN'], 12), PHEP_TON_NAM_TRUOC: parseSheetNumber(row['PHÉP TỒN NĂM TRƯỚC'], 0), PHEP_DIEU_CHINH: parseSheetNumber(row['PHÉP ĐIỀU CHỈNH'], 0), GHI_CHU: String(row['GHI CHÚ'] ?? ''),
                };
                const errors = [];
                if (!employeeBySoHd.has(soHd)) errors.push(`Không có nhân sự Số HĐ ${soHd}.`);
                if (!(value.NAM >= 2000 && value.NAM <= 2100)) errors.push('Năm không hợp lệ.');
                if ([value.PHEP_CHUAN, value.PHEP_TON_NAM_TRUOC, value.PHEP_DIEU_CHINH].some(Number.isNaN)) errors.push('Số ngày phải là số.');
                if (value.PHEP_CHUAN < 0 || value.PHEP_TON_NAM_TRUOC < 0) errors.push('Phép chuẩn và phép tồn không được âm.');
                if (seen.has(`${soHd}-${value.NAM}`)) errors.push('Trùng nhân sự và năm trong file.');
                seen.add(`${soHd}-${value.NAM}`);
                return { line: index + 2, name: employeeBySoHd.get(soHd)?.HO_TEN || String(row['HỌ TÊN'] ?? ''), value, errors };
            }));
        } catch (error) {
            toast.error(error.message);
        }
    };
    const valid = rows?.filter((row) => !row.errors.length) ?? [];
    return (
        <Modal title="Nhập sổ phép từ Excel" icon={Upload} onClose={onClose} disabled={saving} size="max-w-3xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Đóng</button>
                    <button className={buttonPrimary} disabled={saving || !valid.length}
                        onClick={async () => {
                            try { setSaving(true); const result = await importLeaveBalances(data, valid.map((row) => row.value)); toast.success(`Đã nhập sổ phép: ${result.created} mới, ${result.updated} cập nhật`); onDone(); } catch (error) { toast.error(error.message); } finally { setSaving(false); }
                        }}><Upload className="w-4 h-4" />Nhập {valid.length} dòng</button>
                </>
            )}>
            <div className="space-y-3 text-sm">
                <p className="text-gray-600">Dùng file mẫu (nút <b>Tải file mẫu</b>, đã điền sẵn nhân sự đang làm). Sổ đã có thì được cập nhật, số đã dùng không đổi.</p>
                <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="text-sm" />
                {rows && (
                    <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Dòng</th><th className="px-2 py-1.5 text-left">Nhân sự</th><th className="px-2 py-1.5 text-right">Chuẩn / tồn / chỉnh</th><th className="px-2 py-1.5 text-left">Kết quả</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((row) => (
                                    <tr key={row.line} className={row.errors.length ? 'bg-red-50' : ''}>
                                        <td className="px-2 py-1.5">{row.line}</td>
                                        <td className="px-2 py-1.5">{row.name} · {row.value.NHAN_SU_ID} · {row.value.NAM}</td>
                                        <td className="px-2 py-1.5 text-right">{days(row.value.PHEP_CHUAN)} / {days(row.value.PHEP_TON_NAM_TRUOC)} / {days(row.value.PHEP_DIEU_CHINH)}</td>
                                        <td className={`px-2 py-1.5 ${row.errors.length ? 'text-red-700' : 'text-green-700'}`}>{row.errors.length ? row.errors.join(' ') : 'Hợp lệ'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
}

const PhepNam = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [tab, setTab] = useState('requests');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('ALL');
    const [leaveType, setLeaveType] = useState('ALL');
    const [month, setMonth] = useState('ALL');
    const [form, setForm] = useState(null);
    const [dialog, setDialog] = useState(null);
    const canView = canViewPayrollPage(PAYROLL_PATHS.leave);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadLeaveData());
        } catch (error) {
            console.error('Lỗi tải phép năm:', error);
            toast.error('Không tải được phép năm. Kiểm tra bảng PHEP_NAM, DON_PHEP, LICH_NGAY_LE đã có trên AppSheet chưa.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const employeeBySoHd = useMemo(() => new Map((data?.employees ?? []).map((employee) => [employee.SO_HD, employee])), [data]);
    const years = useMemo(() => {
        const now = new Date().getFullYear();
        return [...new Set([now - 1, now, now + 1, ...(data?.balances ?? []).map((row) => row.NAM), ...(data?.requests ?? []).map((row) => row.NAM)].filter(Boolean))].sort((a, b) => b - a);
    }, [data]);
    const balances = useMemo(() => (data?.balances ?? []).filter((row) => row.NAM === year)
        .map((row) => ({ ...row, employee: employeeBySoHd.get(row.NHAN_SU_ID) }))
        .filter((row) => matchesSearch(`${row.NHAN_SU_ID} ${row.employee?.HO_TEN} ${row.employee?.TO}`, search)), [data, year, employeeBySoHd, search]);
    const requestsOfYear = useMemo(() => (data?.requests ?? []).filter((row) => row.NAM === year), [data, year]);
    const requests = useMemo(() => requestsOfYear
        .map((row) => ({ ...row, employee: employeeBySoHd.get(row.NHAN_SU_ID) }))
        .filter((row) => (status === 'ALL' || row.TRANG_THAI === status) && (leaveType === 'ALL' || row.LOAI_PHEP === leaveType)
            && (month === 'ALL' || row.TU_NGAY?.getMonth() + 1 === Number(month) || row.DEN_NGAY?.getMonth() + 1 === Number(month))
            && matchesSearch(`${row.MA_DON} ${row.NHAN_SU_ID} ${row.employee?.HO_TEN} ${row.LY_DO}`, search)), [requestsOfYear, employeeBySoHd, status, leaveType, month, search]);
    const balanceAccessors = useMemo(() => ({
        employee: (row) => row.employee?.HO_TEN ?? row.NHAN_SU_ID, entitled: (row) => row.entitled, used: (row) => row.used, pending: (row) => row.pending, remaining: (row) => row.remaining,
    }), []);
    const requestAccessors = useMemo(() => ({
        code: (row) => row.MA_DON, employee: (row) => row.employee?.HO_TEN ?? row.NHAN_SU_ID, from: (row) => row.TU_NGAY, days: (row) => row.SO_NGAY ?? 0, status: (row) => row.TRANG_THAI, type: (row) => row.LOAI_PHEP,
    }), []);
    const balanceSort = useSortedRows(balances, balanceAccessors, { key: 'employee', direction: 'asc' });
    const requestSort = useSortedRows(requests, requestAccessors, { key: 'from', direction: 'desc' });
    const balancePage = usePagination(balanceSort.sorted);
    const requestPage = usePagination(requestSort.sorted);

    if (!canView) return <AccessDenied />;

    const working = (data?.employees ?? []).filter(isWorking);
    const withoutBalance = working.filter((employee) => !(data?.balances ?? []).some((row) => row.NAM === year && row.NHAN_SU_ID === employee.SO_HD)).length;
    const yearBalances = (data?.balances ?? []).filter((row) => row.NAM === year);
    const sum = (field) => yearBalances.reduce((total, row) => total + (row[field] || 0), 0);
    const run = async (action, successMessage) => {
        try {
            setBusy(true);
            await action();
            if (successMessage) toast.success(successMessage);
            await reload();
            return true;
        } catch (error) {
            toast.error(error.message);
            return false;
        } finally {
            setBusy(false);
        }
    };
    const openRequest = (request, isNew) => setForm({
        type: 'request', isNew,
        initial: {
            MA_DON: request.MA_DON || '', NHAN_SU_ID: request.NHAN_SU_ID || '', LOAI_PHEP: request.LOAI_PHEP || 'PHEP_NAM',
            from: toInputDate(request.TU_NGAY || new Date()), to: request.DEN_NGAY && request.TU_NGAY && request.DEN_NGAY.getTime() !== request.TU_NGAY.getTime() ? toInputDate(request.DEN_NGAY) : '',
            halfDay: request.SO_NGAY === 0.5, LY_DO: request.LY_DO || '',
        },
    });
    const openBalance = (balance, isNew) => setForm({
        type: 'balance', isNew,
        initial: { NHAN_SU_ID: balance.NHAN_SU_ID || '', NAM: year, standard: String(balance.PHEP_CHUAN ?? 12), carry: String(balance.PHEP_TON_NAM_TRUOC ?? 0), adjust: String(balance.PHEP_DIEU_CHINH ?? 0), GHI_CHU: balance.GHI_CHU || '' },
    });
    const downloadTemplate = () => downloadWorkbook(`Mau_SoPhep_${year}.xlsx`, [
        { name: 'Sổ phép', rows: [BALANCE_HEADER, ...working.map((employee) => {
            const current = yearBalances.find((row) => row.NHAN_SU_ID === employee.SO_HD);
            return [employee.SO_HD, employee.HO_TEN, year, current?.PHEP_CHUAN ?? 12, current?.PHEP_TON_NAM_TRUOC ?? 0, current?.PHEP_DIEU_CHINH ?? 0, current?.GHI_CHU ?? ''];
        })] },
    ]);

    const requestActions = (row) => {
        if (!canManage) return null;
        return (
            <div className="flex justify-center gap-1">
                {['NHAP', 'TU_CHOI'].includes(row.TRANG_THAI) && (
                    <>
                        <IconAction icon={Send} title="Gửi duyệt" tone="blue" disabled={busy} onClick={() => run(() => submitLeaveRequest(row), 'Đã gửi duyệt đơn nghỉ')} />
                        <IconAction icon={Edit} title="Sửa" disabled={busy} onClick={() => openRequest(row, false)} />
                        <IconAction icon={Trash} title="Xóa" tone="red" disabled={busy} onClick={() => setDialog({ type: 'delete', request: row })} />
                    </>
                )}
                {row.TRANG_THAI === 'CHO_DUYET' && (
                    <>
                        <IconAction icon={CheckCircle2} title="Duyệt" tone="green" disabled={busy}
                            onClick={() => run(() => approveLeaveRequest(data, row), 'Đã duyệt đơn nghỉ')} />
                        <IconAction icon={XCircle} title="Từ chối" tone="red" disabled={busy} onClick={() => setDialog({ type: 'reject', request: row })} />
                    </>
                )}
                {row.TRANG_THAI === 'DA_DUYET' && <IconAction icon={Ban} title="Hủy đơn đã duyệt" tone="amber" disabled={busy} onClick={() => setDialog({ type: 'cancel', request: row })} />}
            </div>
        );
    };
    const period = (row) => (row.DEN_NGAY && row.DEN_NGAY.getTime() !== row.TU_NGAY.getTime() ? `${formatDay(row.TU_NGAY)} – ${formatDay(row.DEN_NGAY)}` : formatDay(row.TU_NGAY));
    const statusBadge = (row) => {
        const info = LEAVE_STATUSES[row.TRANG_THAI] || LEAVE_STATUSES.NHAP;
        return <Badge tone={info.tone} title={row.LY_DO_TU_CHOI || undefined}>{info.label}</Badge>;
    };
    const employeeCell = (row) => <><div className="font-medium text-gray-900">{row.employee?.HO_TEN || row.NHAN_SU_ID}</div><div className="text-xs text-gray-500">{row.NHAN_SU_ID}{row.employee?.TO ? ` · ${row.employee.TO}` : ''}</div></>;

    return (
        <PageShell icon={CalendarCheck2} title="Phép năm" subtitle="Sổ phép từng người và đơn nghỉ; chỉ đơn đã duyệt mới vào ngày phép hưởng lương (PN) của kỳ"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && (
                        <>
                            <button className={buttonSecondary} onClick={() => setDialog({ type: 'init' })} disabled={!data || busy}><BookOpen className="w-4 h-4" />Khởi tạo sổ phép {year}</button>
                            <button className={buttonSecondary} onClick={downloadTemplate} disabled={!data}><FileSpreadsheet className="w-4 h-4" />Tải file mẫu</button>
                            <button className={buttonSecondary} onClick={() => setDialog({ type: 'import' })} disabled={!data || busy}><Upload className="w-4 h-4" />Nhập sổ phép</button>
                            <button className={buttonPrimary} onClick={() => openRequest({}, true)} disabled={!data || busy}><Plus className="w-4 h-4" />Tạo đơn nghỉ</button>
                        </>
                    )}
                </>
            )}>
            <StatCards active={status} onSelect={(value) => { setTab('requests'); setStatus(value); }} cards={[
                { key: 'people', label: 'Nhân sự có sổ', value: yearBalances.length, tone: 'indigo', caption: withoutBalance ? `${withoutBalance} người đang làm chưa có sổ` : 'Đủ sổ cho người đang làm' },
                { key: 'entitled', label: 'Tổng được dùng', value: days(sum('entitled')), tone: 'green' },
                { key: 'used', label: 'Đã dùng', value: days(sum('used')), tone: 'yellow' },
                { key: 'remaining', label: 'Còn lại', value: days(sum('remaining')), tone: 'blue' },
                { key: 'pending', label: 'Đơn chờ duyệt', value: requestsOfYear.filter((row) => row.TRANG_THAI === 'CHO_DUYET').length, tone: 'purple', filterValue: 'CHO_DUYET' },
            ]} />
            <div className="flex gap-1 mb-3 border-b border-gray-200">
                {[['requests', `Đơn nghỉ (${requestsOfYear.length})`], ['balances', `Sổ phép (${yearBalances.length})`]].map(([value, label]) => (
                    <button key={value} type="button" onClick={() => setTab(value)}
                        className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === value ? 'border-indigo-600 text-indigo-700 font-semibold' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>{label}</button>
                ))}
            </div>
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm nhân sự, số HĐ, mã đơn, lý do..."
                filterCount={tab === 'requests' ? [status, leaveType, month].filter((value) => value !== 'ALL').length : 0}
                onClearFilters={() => { setStatus('ALL'); setLeaveType('ALL'); setMonth('ALL'); setSearch(''); }}
                extra={(
                    <select className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Năm">
                        {years.map((item) => <option key={item} value={item}>Năm {item}</option>)}
                    </select>
                )}
                filters={tab === 'requests' ? (
                    <>
                        <FilterField label="Trạng thái">
                            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="ALL">Tất cả trạng thái</option>
                                {Object.entries(LEAVE_STATUSES).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Loại nghỉ">
                            <select className={inputClass} value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
                                <option value="ALL">Tất cả loại</option>
                                {Object.entries(LEAVE_TYPES).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Tháng">
                            <select className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="ALL">Tất cả tháng</option>
                                {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                            </select>
                        </FilterField>
                    </>
                ) : undefined} />
            {loading || !data ? <Spinner /> : tab === 'balances' ? (
                balanceSort.sorted.length === 0 ? (
                    <EmptyState icon={BookOpen} title={`Chưa có sổ phép năm ${year}`}>Bấm <b>Khởi tạo sổ phép {year}</b> hoặc nhập từ Excel.</EmptyState>
                ) : (
                    <>
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className={tableClass}>
                                <thead className={theadClass}>
                                    <tr>
                                        <SortableTh label="Nhân sự" sortKey="employee" sort={balanceSort.sort} onSort={balanceSort.toggleSort} />
                                        <th className={`${thClass} text-right`}>Chuẩn</th>
                                        <th className={`${thClass} text-right`}>Tồn năm trước</th>
                                        <th className={`${thClass} text-right`}>Điều chỉnh</th>
                                        <SortableTh label="Được dùng" sortKey="entitled" sort={balanceSort.sort} onSort={balanceSort.toggleSort} align="right" />
                                        <SortableTh label="Đã dùng" sortKey="used" sort={balanceSort.sort} onSort={balanceSort.toggleSort} align="right" />
                                        <SortableTh label="Chờ duyệt" sortKey="pending" sort={balanceSort.sort} onSort={balanceSort.toggleSort} align="right" />
                                        <SortableTh label="Còn lại" sortKey="remaining" sort={balanceSort.sort} onSort={balanceSort.toggleSort} align="right" />
                                        {canManage && <th className={`${thClass} text-center`}>Sửa</th>}
                                    </tr>
                                </thead>
                                <tbody className={tbodyClass}>
                                    {balancePage.pageRows.map((row) => (
                                        <tr key={row.ID} className="hover:bg-gray-50">
                                            <td className={tdClass}>{employeeCell(row)}</td>
                                            <td className={`${tdClass} text-right`}>{days(row.PHEP_CHUAN)}</td>
                                            <td className={`${tdClass} text-right`}>{days(row.PHEP_TON_NAM_TRUOC)}</td>
                                            <td className={`${tdClass} text-right`}>{days(row.PHEP_DIEU_CHINH)}</td>
                                            <td className={`${tdClass} text-right font-medium`}>{days(row.entitled)}</td>
                                            <td className={`${tdClass} text-right`}>{days(row.used)}</td>
                                            <td className={`${tdClass} text-right text-yellow-700`}>{row.pending ? days(row.pending) : ''}</td>
                                            <td className={`${tdClass} text-right font-bold ${row.remaining < 0 ? 'text-red-700' : 'text-green-800'}`}>{days(row.remaining)}</td>
                                            {canManage && <td className={tdClass}><div className="flex justify-center"><IconAction icon={Edit} title="Sửa sổ phép" onClick={() => openBalance(row, false)} disabled={busy} /></div></td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination pagination={balancePage.pagination} unit="sổ phép" />
                    </>
                )
            ) : requestSort.sorted.length === 0 ? (
                <EmptyState icon={CalendarCheck2} title="Chưa có đơn nghỉ phù hợp">Bấm <b>Tạo đơn nghỉ</b> để ghi ngày nghỉ phép của nhân sự.</EmptyState>
            ) : (
                <>
                    <div className="space-y-3 md:hidden">
                        {requestPage.pageRows.map((row) => (
                            <div key={row.MA_DON} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start gap-2"><div>{employeeCell(row)}</div>{statusBadge(row)}</div>
                                <p className="text-sm text-gray-700 mt-2">{LEAVE_TYPES[row.LOAI_PHEP]?.label || row.LOAI_PHEP} · {period(row)} · <b>{days(row.SO_NGAY)} ngày</b></p>
                                {row.LY_DO && <p className="text-xs text-gray-500">{row.LY_DO}</p>}
                                <div className="flex justify-end mt-2">{requestActions(row)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Nhân sự" sortKey="employee" sort={requestSort.sort} onSort={requestSort.toggleSort} />
                                    <SortableTh label="Loại" sortKey="type" sort={requestSort.sort} onSort={requestSort.toggleSort} />
                                    <SortableTh label="Thời gian" sortKey="from" sort={requestSort.sort} onSort={requestSort.toggleSort} />
                                    <SortableTh label="Số ngày" sortKey="days" sort={requestSort.sort} onSort={requestSort.toggleSort} align="right" />
                                    <th className={thClass}>Lý do</th>
                                    <SortableTh label="Trạng thái" sortKey="status" sort={requestSort.sort} onSort={requestSort.toggleSort} />
                                    <th className={thClass}>Người lập / duyệt</th>
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {requestPage.pageRows.map((row) => (
                                    <tr key={row.MA_DON} className="hover:bg-gray-50">
                                        <td className={tdClass}>{employeeCell(row)}</td>
                                        <td className={tdClass}>{LEAVE_TYPES[row.LOAI_PHEP]?.label || row.LOAI_PHEP}</td>
                                        <td className={`${tdClass} whitespace-nowrap`}>{period(row)}</td>
                                        <td className={`${tdClass} text-right font-semibold`}>{days(row.SO_NGAY)}</td>
                                        <td className={`${tdClass} max-w-xs`}><div className="truncate" title={row.LY_DO}>{row.LY_DO}</div>{row.LY_DO_TU_CHOI && <div className="text-xs text-red-600">{row.TRANG_THAI === 'HUY' ? 'Lý do hủy' : 'Từ chối'}: {row.LY_DO_TU_CHOI}</div>}</td>
                                        <td className={tdClass}>{statusBadge(row)}</td>
                                        <td className={`${tdClass} text-xs`}>{row.NGUOI_TAO}{row.NGUOI_DUYET && <div className="text-gray-500">{row.NGUOI_DUYET} · {formatDay(row.NGAY_DUYET)}</div>}</td>
                                        <td className={tdClass}>{requestActions(row)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={requestPage.pagination} unit="đơn" />
                </>
            )}
            {form?.type === 'request' && <LeaveRequestForm {...form} data={data} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
            {form?.type === 'balance' && <BalanceForm {...form} employees={working} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
            {dialog?.type === 'init' && (
                <InitializeModal year={year} onClose={() => setDialog(null)}
                    onSubmit={async (standard, carryOver) => {
                        let count = 0;
                        const ok = await run(async () => { count = await initializeLeaveYear(data, year, standard, { carryOver }); });
                        if (ok) { setDialog(null); setTab('balances'); toast.success(count ? `Đã tạo ${count} sổ phép năm ${year}` : `Mọi người đang làm đã có sổ phép năm ${year}`); }
                    }} />
            )}
            {dialog?.type === 'import' && <ImportBalanceModal data={data} year={year} onClose={() => setDialog(null)} onDone={() => { setDialog(null); setTab('balances'); reload(); }} />}
            {dialog?.type === 'reject' && (
                <ReasonModal title="Từ chối đơn nghỉ" label="Lý do từ chối" confirmLabel="Từ chối" onClose={() => setDialog(null)}
                    onSubmit={async (reason) => { if (await run(() => rejectLeaveRequest(dialog.request, reason), 'Đã từ chối đơn nghỉ')) setDialog(null); }} />
            )}
            {dialog?.type === 'cancel' && (
                <ReasonModal title="Hủy đơn đã duyệt" label="Lý do hủy (số phép được trả lại)" confirmLabel="Hủy đơn" onClose={() => setDialog(null)}
                    onSubmit={async (reason) => { if (await run(() => cancelLeaveRequest(data, dialog.request, reason), 'Đã hủy đơn, số phép được trả lại')) setDialog(null); }} />
            )}
            {dialog?.type === 'delete' && (
                <ConfirmDialog title="Xóa đơn nghỉ" confirmLabel="Xóa" onClose={() => setDialog(null)}
                    message={<>Xóa đơn nghỉ của <b>{dialog.request.employee?.HO_TEN || dialog.request.NHAN_SU_ID}</b> ({period(dialog.request)})?</>}
                    onConfirm={async () => { if (await run(() => deleteLeaveRequest(dialog.request), 'Đã xóa đơn nghỉ')) setDialog(null); }} />
            )}
        </PageShell>
    );
};

export default PhepNam;
